import { Head, router } from "@inertiajs/react"
import { CircleCheck, CirclePause, Ellipsis } from "lucide-react"
import { useEffect, useState } from "react"

import { InboxPanel } from "@/components/agents/dashboard/review-responder/inbox-panel"
import { PostedReviewModal } from "@/components/agents/dashboard/review-responder/posted-review-modal"
import { ReviewDraftModal } from "@/components/agents/dashboard/review-responder/review-draft-modal"
import type {
  DashboardDraft,
  DashboardReview,
  ReviewResponderDashboardStat,
} from "@/components/agents/dashboard/review-responder/types"
import { ReviewResponderEditSheet } from "@/components/agents/edit/review-responder/edit-sheet"
import { PageHeadingBar, PageLayout } from "@/components/page-layout"
import { StatCard } from "@/components/partner-dashboard/stat-card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import {
  approveAgentReviewDraftPath,
  cancelAgentPath,
  dismissAgentReviewDraftPath,
  pauseAgentPath,
  restoreAgentReviewDraftPath,
  resumeAgentPath,
  undoCancellationAgentPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Props {
  agent: {
    uid: string
    name: string
    state: string
    setup_data: Record<string, unknown>
  }
  metrics: ReviewResponderDashboardStat[]
  pending_drafts: DashboardDraft[]
  resolved_reviews: DashboardReview[]
  scheduled_cancellation: { deactivates_on: string } | null
}

function StatusBadge({ state }: { state: string }) {
  if (state === "operational") {
    return (
      <span className="flex items-center gap-3 text-sm/6">
        <CircleCheck className="size-5 shrink-0 fill-green-500 text-white" />
        <span className="font-medium text-green-700">Active</span>
      </span>
    )
  }
  if (state === "paused") {
    return (
      <span className="flex items-center gap-3 text-sm/6">
        <CirclePause className="size-5 shrink-0 fill-amber-500 text-white" />
        <span className="font-medium text-amber-700">Paused</span>
      </span>
    )
  }
  return (
    <span className="flex items-center gap-3 text-sm/6">
      <span className="bg-muted-foreground/40 inline-block size-2 rounded-full" />
      <span className="text-muted-foreground font-medium capitalize">
        {state}
      </span>
    </span>
  )
}

export default function ReviewResponderDashboard({
  agent,
  metrics,
  pending_drafts,
  resolved_reviews,
  scheduled_cancellation,
}: Props) {
  const [cancelOpen, setCancelOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [activeDraftUid, setActiveDraftUid] = useState<string | null>(null)
  const [resolvedUid, setResolvedUid] = useState<string | null>(null)

  // Deep-link: the notification email links to ?draft=<uid>; open it in focus.
  useEffect(() => {
    const uid = new URLSearchParams(window.location.search).get("draft")
    if (uid && pending_drafts.some((d) => d.uid === uid)) {
      setActiveDraftUid(uid)
    }
    // Run once on mount — a later prop refresh shouldn't reopen the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeIdx = pending_drafts.findIndex((d) => d.uid === activeDraftUid)
  const activeDraft = activeIdx >= 0 ? pending_drafts[activeIdx] : null
  const resolvedIdx = resolved_reviews.findIndex((r) => r.uid === resolvedUid)
  const resolvedReview = resolvedIdx >= 0 ? resolved_reviews[resolvedIdx] : null

  // The item that slides into this slot once `uid` leaves the queue, so acting
  // on a draft auto-advances to the next (conveyor flow).
  function nextAfter(uid: string): string | null {
    const idx = pending_drafts.findIndex((d) => d.uid === uid)
    if (idx === -1) return null
    return (pending_drafts[idx + 1] ?? pending_drafts[idx - 1])?.uid ?? null
  }

  function act(
    path: string,
    payload: Record<string, string>,
    onDone: () => void,
  ) {
    setProcessing(true)
    router.post(path, payload, {
      preserveScroll: true,
      onSuccess: onDone,
      onFinish: () => setProcessing(false),
    })
  }

  function approveDraft(uid: string, text: string) {
    const next = nextAfter(uid)
    act(approveAgentReviewDraftPath(agent.uid, uid), { body: text }, () =>
      setActiveDraftUid(next),
    )
  }

  function dismissDraft(uid: string) {
    const next = nextAfter(uid)
    act(dismissAgentReviewDraftPath(agent.uid, uid), {}, () =>
      setActiveDraftUid(next),
    )
  }

  function restoreDraft(draftUid: string) {
    act(restoreAgentReviewDraftPath(agent.uid, draftUid), {}, () =>
      setResolvedUid(null),
    )
  }

  function navDraft(delta: number) {
    const next = pending_drafts[activeIdx + delta]
    if (next) setActiveDraftUid(next.uid)
  }

  function navResolved(delta: number) {
    const next = resolved_reviews[resolvedIdx + delta]
    if (next) setResolvedUid(next.uid)
  }

  const paused = agent.state === "paused"
  const canceling = scheduled_cancellation != null
  const deactivatesOn = scheduled_cancellation
    ? new Date(scheduled_cancellation.deactivates_on).toLocaleDateString(
        undefined,
        { month: "short", day: "numeric", year: "numeric" },
      )
    : null

  const breadcrumbs: BreadcrumbItem[] = [
    { title: agent.name, href: `/agents/${agent.uid}` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${agent.name} — review inbox`} />

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {agent.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The agent keeps working until the end of your current billing
              period, then stops. Your review history and configuration are
              kept, and you can undo this any time before then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => router.post(cancelAgentPath(agent.uid))}
            >
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReviewResponderEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        agentUid={agent.uid}
        agentName={agent.name}
        setupData={agent.setup_data}
      />

      <ReviewDraftModal
        open={activeDraftUid !== null}
        onOpenChange={(v) => {
          if (!v) setActiveDraftUid(null)
        }}
        draft={activeDraft}
        index={activeIdx}
        total={pending_drafts.length}
        processing={processing}
        onPrev={() => navDraft(-1)}
        onNext={() => navDraft(1)}
        onApprove={approveDraft}
        onDismiss={dismissDraft}
      />

      <PostedReviewModal
        open={resolvedUid !== null}
        onOpenChange={(v) => {
          if (!v) setResolvedUid(null)
        }}
        review={resolvedReview}
        index={resolvedIdx}
        total={resolved_reviews.length}
        processing={processing}
        onPrev={() => navResolved(-1)}
        onNext={() => navResolved(1)}
        onRestore={restoreDraft}
      />

      <PageLayout>
        <PageHeadingBar heading={agent.name}>
          <StatusBadge state={agent.state} />
          <div className="flex gap-4">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-sm px-2"
                  aria-label="Agent actions"
                >
                  <Ellipsis className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() =>
                    router.post(
                      paused
                        ? resumeAgentPath(agent.uid)
                        : pauseAgentPath(agent.uid),
                    )
                  }
                >
                  {paused ? "Resume" : "Pause"}
                </DropdownMenuItem>
                {!canceling && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={(e) => {
                        e.preventDefault()
                        setCancelOpen(true)
                      }}
                    >
                      Cancel
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </PageHeadingBar>

        {paused && !canceling && (
          <div className="text-muted-foreground rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900/60 dark:bg-amber-950/20">
            The agent is paused — no new reviews are answered and nothing is
            posted. Resume when you&rsquo;re ready.
          </div>
        )}

        {canceling && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900/60 dark:bg-amber-950/20">
            <span className="text-muted-foreground">
              This agent will stop on {deactivatesOn}. It keeps working until
              then.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.post(undoCancellationAgentPath(agent.uid))}
            >
              Keep agent
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {metrics.map((stat) => (
            <StatCard
              key={stat.key}
              title={stat.title}
              value={stat.value}
              change={stat.change ?? undefined}
              subLabel={stat.subLabel}
              className="shadow-none"
            />
          ))}
        </div>

        <InboxPanel
          drafts={pending_drafts}
          reviews={resolved_reviews}
          onSelectDraft={setActiveDraftUid}
          onSelectResolved={setResolvedUid}
        />
      </PageLayout>
    </AppLayout>
  )
}
